/**
 * Keeps the most recent frontend errors and warnings of this browser tab in memory, so the
 * developer dashboard can show them. Nothing is stored or sent: Sentry is the full history.
 */

export type FrontendErrorLevel = 'error' | 'warning';

export type FrontendError = {
  occurredAt: Date;
  level: FrontendErrorLevel;
  message: string;
  source: string;
};

export const FRONTEND_ERROR_CAPACITY = 100;

export type FrontendErrorStore = {
  add: (error: FrontendError) => void;
  /** Returns the errors, newest first. The array changes only when an error is added. */
  getAll: () => readonly FrontendError[];
  subscribe: (listener: () => void) => () => void;
};

export const createFrontendErrorStore = (
  capacity = FRONTEND_ERROR_CAPACITY,
): FrontendErrorStore => {
  let errors: readonly FrontendError[] = [];
  const listeners = new Set<() => void>();

  return {
    add: (error) => {
      errors = [error, ...errors].slice(0, capacity);
      for (const listener of listeners) listener();
    },
    getAll: () => errors,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
};

export const frontendErrorStore = createFrontendErrorStore();

const describe = (value: unknown): string => {
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
};

type ErrorEventTarget = Pick<Window, 'addEventListener'>;

/**
 * Records uncaught errors and unhandled promise rejections of the target window.
 */
export const captureWindowErrors = (
  target: ErrorEventTarget,
  store: FrontendErrorStore,
  now: () => Date = () => new Date(),
): void => {
  target.addEventListener('error', (event: ErrorEvent) => {
    store.add({
      occurredAt: now(),
      level: 'error',
      message: describe(event.error ?? event.message),
      source: 'window.onerror',
    });
  });
  target.addEventListener(
    'unhandledrejection',
    (event: PromiseRejectionEvent) => {
      store.add({
        occurredAt: now(),
        level: 'error',
        message: describe(event.reason),
        source: 'unhandledrejection',
      });
    },
  );
};

/** The part of a Sentry event that the dashboard uses. */
export type SentryEventLike = {
  level?: string;
  message?: string;
  exception?: { values?: { type?: string; value?: string }[] };
  timestamp?: number;
};

/**
 * Converts a Sentry event to a dashboard entry. Returns null for events below warning level.
 */
export const fromSentryEvent = (
  event: SentryEventLike,
  now: () => Date = () => new Date(),
): FrontendError | null => {
  const level: FrontendErrorLevel | null =
    event.level === 'warning'
      ? 'warning'
      : event.level === undefined ||
          event.level === 'error' ||
          event.level === 'fatal'
        ? 'error'
        : null;
  if (level === null) return null;

  const exception = event.exception?.values?.[0];
  const message = exception
    ? `${exception.type ?? 'Error'}: ${exception.value ?? ''}`
    : (event.message ?? 'Unknown event');

  return {
    occurredAt: event.timestamp ? new Date(event.timestamp * 1000) : now(),
    level,
    message,
    source: 'sentry',
  };
};
